import { inject, Injectable, signal } from '@angular/core';
import { Language } from '@app/shared/models/app.model';
import { LanguageService } from './language.service';
import { Observable } from 'rxjs';
import { restrictLanguagesForLicenses } from './language-restrictions';
import { SettingsService } from './settings.service';
import { SpeakseeOrderService } from '@app/modules/license/license.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { UpdateMicrophoneSettingsRequest } from './settings-api.service';

@Injectable({
  providedIn: 'root'
})
export class LanguageSettingsService {
  private _selectedLanguage = signal<Language | null>(null);
  public seletecedLanguage = this._selectedLanguage.asReadonly();
  
  private selectedLanguage$ = toObservable(this._selectedLanguage);

  private languageService = inject(LanguageService);
  private settingsService = inject(SettingsService);
  private translocoService = inject(TranslocoService);
  private orderService = inject(SpeakseeOrderService);

  /**
   * Gets all available languages from the language service and sorts them.
   * The result is passed through the per-device restriction allowlist, so a
   * user who owns a restricted kit (see language-restrictions.ts) only ever
   * sees their allowed languages; everyone else gets the full list.
   * @returns A sorted array of all languages with icons
   */
  getAllLanguages(): Language[] {
    const languages = this.languageService.languages
      .filter((lang) => lang.iconClass !== '')
      .sort((a, b) => {
        const textA = a.exonym.toUpperCase();
        const textB = b.exonym.toUpperCase();
        return textA < textB ? -1 : textA > textB ? 1 : 0;
      })
      .sort((a, b) => {
        const boolA = a.isVerified;
        const boolB = b.isVerified;
        return boolA && !boolB ? -1 : !boolA && boolB ? 1 : 0;
      });

    return restrictLanguagesForLicenses(this.orderService.licenses, languages);
  }

  /**
   * Gets verified languages (languages marked as verified)
   * @returns An array of verified languages
   */
  getVerifiedLanguages(): Language[] {
    return this.getAllLanguages().filter((x) => x.isVerified);
  }

  /**
   * Gets unverified languages
   * @returns An array of unverified languages
   */
  getUnverifiedLanguages(): Language[] {
    return this.getAllLanguages().filter((x) => !x.isVerified);
  }
  /**
   * Selects a language and sets it as the translation language
   * @param language The language to select
   */
  selectTranslationLanguage(language: Language): void {
    this._selectedLanguage.set(language);
    this.languageService.setTranslationLanguage(language.languageCode);
    this.settingsService.changeLocalLanguageOut(language.languageCode);
  }

  /**
   * Gets the currently selected language as an Observable
   * @returns An Observable of the currently selected language
   */
  getSelectedLanguage$(): Observable<Language | null> {
    return this.selectedLanguage$;
  }

  /**
   * Detects the user's device language and tries to select a matching language
   * @returns The detected language or null if none was found
   */
  detectDeviceLanguage(): Language | null {
    // Get browser language (e.g., "en-US", "nl", "fr-FR")
    const browserLang = navigator.language;

    // Try to find an exact match first
    const exactMatch = this.languageService.getLangFromCode(browserLang);
    if (exactMatch) {
      this.selectTranslationLanguage(exactMatch);
      return exactMatch;
    }

    // If no exact match, try to find a match for the language part (e.g., "en" from "en-US")
    const langPart = browserLang.split('-')[0];
    const similarLang = this.getAllLanguages().find((lang) =>
      lang.languageCode.toLowerCase().startsWith(langPart.toLowerCase())
    );

    if (similarLang) {
      this.selectTranslationLanguage(similarLang);
      return similarLang;
    } 
    
    // Default to English if no match
    const defaultLang = this.languageService.getLangFromCode('en-US');
    if (defaultLang) {
      this.selectTranslationLanguage(defaultLang);
      return defaultLang;
    }

    return null;
  }

  /**
   * Gets the language service's current language observable
   * @returns Observable of the current language selection
   */
  getCurrentLanguage$() {
    return this.languageService.currentLanguage$;
  }

  /**
   * Updates the language settings for a microphone
   * @param micId The ID of the microphone to update
   * @param language The language to set
   * @param type Whether this is an input ('in') or output ('out') language
   * @param updateAllMics Whether to update all microphones or just the specified one
   * @returns Observable of the update operation result
   */
  updateMicrophoneLanguage(
    micId: number, 
    language: Language, 
    type: 'in' | 'out',
    updateAllMics: boolean = false,
    microphones?: number[]
  ): Observable<Record<string, unknown>> {
    // Create the update request
    const micUpdate: Omit<UpdateMicrophoneSettingsRequest, 'event'> = {
      microphones: []
    };
    
    // Determine which microphones to update
    const micsToUpdate = microphones || [micId];
    
    // Add each microphone to the update request
    for (const id of micsToUpdate) {
      micUpdate.microphones.push({
        mic_id: id,
        [`language_${type}`]: language.languageCode
      } as any);
    }
    
    // Always update the selected language when changing output language
    if (type === 'out') {
      this.selectTranslationLanguage(language);
    }
    return this.settingsService.updateMicSettings(micUpdate);
  }

    /**
   * Set the Transloco translation service language
   */
  setTranslocoLanguage(langCode: string): void {
    if (['en', 'nl', 'de', 'fr'].includes(langCode)) {
      this.translocoService.setActiveLang(langCode);
    } else {
      this.translocoService.setActiveLang('en');
    }
  }
}
